import React, { useEffect, useState } from 'react'

import { BasicModal } from '@/components/layout/modals/BasicModal'
import { Button } from '@/components/buttons/Button'
import { TextArea } from '@/components/forms/TextArea'

// =============================================================================
// ResolveCommentModal — the confirm step behind Resolve on a comment card
// (AVR-879). Its reason to exist is the note: resolving is one click, but the
// note is what tells the person who filed the comment *what changed*, and there
// is nowhere else on the card to type one.
//
// The note is optional and stays optional — a PE closing a batch of comments
// should not be made to write prose to do it, so Resolve is always enabled and
// an empty note is sent as no note at all (see resolveChangeRequest).
// =============================================================================

// Mirrors the BE cap on resolution_note (UpdateRequestBody, max_length=8192).
const NOTE_MAX_LENGTH = 8192

const ResolveCommentModal = ({ show, onClose, onConfirm, busy }) => {
  const [note, setNote] = useState('')

  // Reset on open: the modal stays mounted across open/close cycles, so without
  // this the next comment you resolve starts with the previous one's note.
  useEffect(() => {
    if (show) {
      setNote('')
    }
  }, [show])

  const confirm = () => {
    if (!busy) {
      onConfirm(note.trim())
    }
  }

  // ⌘↵ submits, matching CommentComposer — the same hands typing the note.
  const handleKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault()
      confirm()
    }
  }

  return (
    <BasicModal
      show={show}
      onClose={onClose}
      title="Resolve comment"
      size="small"
      footer={
        <div className="form-actions">
          <Button onClick={onClose} data-testid="resolve-comment-cancel">
            Cancel
          </Button>
          <Button
            brand
            disabled={busy}
            loading={busy}
            onClick={confirm}
            data-testid="resolve-comment-submit"
          >
            Resolve
          </Button>
        </div>
      }
    >
      <div className="voice-agents__resolve-comment-form" data-testid="resolve-comment-form">
        <p className="voice-agents__resolve-comment-hint">
          The comment moves to Resolved everywhere it appears — here and on the call it was filed
          from. You can reopen it later.
        </p>
        <label className="voice-agents__resolve-comment-field">
          <span className="voice-agents__resolve-comment-label">
            Resolution note{' '}
            <span className="voice-agents__resolve-comment-optional">(optional)</span>
          </span>
          <TextArea
            value={note}
            rows={3}
            maxLength={NOTE_MAX_LENGTH}
            onChange={(event) => setNote(event.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What changed, or why this needed no change"
            aria-label="Resolution note"
            data-testid="resolve-comment-note"
            autoFocus
          />
        </label>
      </div>
    </BasicModal>
  )
}

export default ResolveCommentModal
